import { useMemo, useReducer } from 'react';
import { SyberryTodayPlanningStep, type SyberryTodayPlanning, type SyberryTodayTaskId } from '../types';
import { syberryTodayPlanningReducer } from './reducer';
import { SyberryTodayPlanningActionType, type SyberryTodaySyncPlanningOptions } from './types';
import { createEmptySyberryTodayPlanningState, getSyberryTodayPlanningStep, selectTasksByIds } from './utils';

const useSyberryTodayPlanningState = () => {
  const [state, dispatch] = useReducer(syberryTodayPlanningReducer, createEmptySyberryTodayPlanningState());

  const selectedTasks = useMemo(
    () => selectTasksByIds(state.tasks, state.selectedTaskIds),
    [state.tasks, state.selectedTaskIds],
  );
  const activeStep = getSyberryTodayPlanningStep(state.planStatus);
  const hasSelectedTasks = state.selectedTaskIds.length > 0;
  const actions = useMemo(
    () => ({
      syncPlanning: (nextPlanning: SyberryTodayPlanning, syncOptions?: SyberryTodaySyncPlanningOptions) =>
        dispatch({ type: SyberryTodayPlanningActionType.SYNC_PLANNING, planning: nextPlanning, syncOptions }),
      setActiveGroup: (groupId: Nullable<string>) =>
        dispatch({ type: SyberryTodayPlanningActionType.SET_ACTIVE_GROUP, groupId }),
      selectTask: (taskId: SyberryTodayTaskId) =>
        dispatch({ type: SyberryTodayPlanningActionType.SELECT_TASK, taskId }),
      deselectTask: (taskId: SyberryTodayTaskId) =>
        dispatch({ type: SyberryTodayPlanningActionType.DESELECT_TASK, taskId }),
      toggleTaskSelection: (taskId: SyberryTodayTaskId) =>
        dispatch({ type: SyberryTodayPlanningActionType.TOGGLE_TASK_SELECTION, taskId }),
      backToTaskSelection: () => dispatch({ type: SyberryTodayPlanningActionType.BACK_TO_TASK_SELECTION }),
      toggleTaskExpansion: (taskId: SyberryTodayTaskId) =>
        dispatch({ type: SyberryTodayPlanningActionType.TOGGLE_TASK_EXPANSION, taskId }),
      openSkipCheckInModal: () => dispatch({ type: SyberryTodayPlanningActionType.OPEN_SKIP_CHECK_IN_MODAL }),
      closeSkipCheckInModal: () => dispatch({ type: SyberryTodayPlanningActionType.CLOSE_SKIP_CHECK_IN_MODAL }),
    }),
    [],
  );

  return {
    state,
    activeStep,
    selectedTasks,
    canConfirmTaskSelection: hasSelectedTasks,
    canConfirmCheckIn: activeStep === SyberryTodayPlanningStep.ORGANIZE_TASKS && hasSelectedTasks,
    actions,
  };
};

export default useSyberryTodayPlanningState;
